/**
 * PulseSkillEffect — Lightning tree Pulse path active.
 *
 * Close-range EM burst that scrambles whatever the target was doing — cancels
 * attacks in progress, applies a very brief stun (200ms), then leaves decision-
 * making disrupted for 0.8 seconds. Low damage. The gap is the point.
 *
 * Pulse fires a small-radius AoE (1.5-tile reach, 0.5-tile radius centered on
 * cursor). On hit it:
 *   1. Calls interruptEnemy to cancel any in-progress attack/ability.
 *   2. Applies a 200ms stun (if not immune — stun has a 300ms immunity window).
 *   3. Applies disruption for `baseDisruptDurationMs + resolver disruptDurationMs`.
 *      Disruption has no immunity window (ADR-065 / lightning-spec CC Hierarchy).
 *   4. Consumes Static charge as bonus damage.
 *   5. If Resonant Pulse (pulse_field) is unlocked, spawns an `em_residue` zone
 *      at the impact point. Enemies entering the zone receive 300ms disruption
 *      (no immunity window). Zone lasts 1.5 seconds.
 *
 * Seizure replaces Pulse later and inherits Lockdown's resolver keys
 * (`disruptDurationMs`, `rangeMultiplier`) via `replacesId: "pulse"` wiring in
 * GameSceneSkillFactory. From Pulse's perspective, it simply queries the 'pulse'
 * resolver scope — Seizure does the same. Resonant Pulse's residue keys also
 * inherit into Seizure via the same resolver scope.
 *
 * Upgrade keys (via resolver, scope: 'pulse'):
 * - disruptDurationMs: additive — added to base 800ms disruption duration
 * - rangeMultiplier: multiplicative — multiplied against base 1.5-tile reach
 * - residueRadiusTiles: direct — Resonant Pulse zone radius (1 tile)
 * - residueDurationMs: direct — Resonant Pulse zone lifetime (1500ms)
 * - residueDisruptMs: direct — Resonant Pulse disruption tick duration (300ms)
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md § Pulse
 * @see lightning-spec.md § Resonant Pulse
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PulseAoEConfig {
  /** Reach from cursor/player in tiles. */
  readonly rangeTiles: number;
  /** Burst radius in tiles (enemies within this of the aim point are hit). */
  readonly radiusTiles: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

/** Position and hit count returned by aoe.hit(). */
export interface PulseHitResult {
  /** Number of enemies hit (0 = miss). */
  readonly enemiesHit: number;
  /** World X coordinate of the AoE impact point (aim position). */
  readonly hitX: number;
  /** World Y coordinate of the AoE impact point (aim position). */
  readonly hitY: number;
}

/**
 * Zone configuration passed to deps.zones.create() when Resonant Pulse fires.
 * Maps to ZoneSystem.ZoneConfig fields. The dotDefId 'disruption' causes the
 * integration layer to route dot_tick events to StatusEffectSystem.apply().
 */
export interface PulseZoneConfig {
  readonly type: string;
  readonly x: number;
  readonly y: number;
  readonly radiusTiles: number;
  readonly durationMs: number;
  /** Tick interval in ms for periodic status application inside the zone. */
  readonly tickIntervalMs: number;
  /** Status effect def ID applied on each tick (e.g. 'disruption'). */
  readonly dotDefId: string;
  /** Duration of each status tick application in ms. */
  readonly dotDurationMs: number;
  readonly sourceSkillId: string;
}

export interface PulseDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly aoe: {
    /**
     * Fire the EM burst. Returns hit count and impact position.
     * In the real integration, queries enemies within radius of the aim point.
     */
    hit(config: PulseAoEConfig): PulseHitResult;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    /**
     * Returns true if the target is currently immune to the given status type.
     * Disruption has no immunity window — only stun/stagger check is needed.
     */
    isImmune(defId: string): boolean;
  };
  readonly charge: {
    /** Consume all Static charge. Returns the amount consumed (0 if empty). */
    consume(chargeId: string): number;
  };
  /**
   * Called for each hit enemy to interrupt any in-progress attack or ability
   * animation, returning the enemy to idle. Real integration calls
   * `enemyAI.interruptCurrentAction()` on each hit enemy reference.
   */
  readonly interruptEnemy?: () => void;
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /**
   * Zone system interface. Required only when Resonant Pulse (pulse_field) is
   * unlocked — PulseSkillEffect calls create() to spawn the em_residue zone.
   * The zone's dotDefId 'disruption' causes the integration layer to route
   * periodic dot_tick events to StatusEffectSystem.apply('disruption', ...).
   */
  readonly zones?: {
    create(config: PulseZoneConfig): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PulseConfig {
  /** Medium cooldown tier: 800ms. */
  readonly cooldownMs: number;
  /** Base disruption duration in ms. Spec: 800ms. */
  readonly baseDisruptDurationMs: number;
  /** Base AoE reach in tiles. Spec: 1.5 tiles. */
  readonly baseRangeTiles: number;
  /** AoE burst radius in tiles. Spec: 0.5 tiles. */
  readonly baseRadiusTiles: number;
  /** Brief stun duration in ms. Spec: 200ms (very short — disruption is the main CC). */
  readonly baseStunDurationMs: number;
}

const DEFAULT_CONFIG: PulseConfig = {
  cooldownMs: 800,
  baseDisruptDurationMs: 800,
  baseRangeTiles: 1.5,
  baseRadiusTiles: 0.5,
  baseStunDurationMs: 200,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PulseSkillEffect implements SkillEffect {
  readonly skillId = 'pulse';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: PulseDeps;
  private readonly config: PulseConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _hitsLanded = 0;
  private _stunApplications = 0;
  private _disruptApplications = 0;
  private _interrupts = 0;
  private _fizzles = 0;
  private _totalChargeConsumed = 0;
  private _resolvedRangeTiles = 0;
  private _resolvedDisruptDurationMs = 0;
  private _zoneSpawns = 0;

  constructor(deps: PulseDeps, config: Partial<PulseConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for simulation assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get hitsLanded(): number { return this._hitsLanded; }
  get stunApplications(): number { return this._stunApplications; }
  get disruptApplications(): number { return this._disruptApplications; }
  get interrupts(): number { return this._interrupts; }
  get fizzles(): number { return this._fizzles; }
  get totalChargeConsumed(): number { return this._totalChargeConsumed; }
  get resolvedRangeTiles(): number { return this._resolvedRangeTiles; }
  get resolvedDisruptDurationMs(): number { return this._resolvedDisruptDurationMs; }
  get zoneSpawns(): number { return this._zoneSpawns; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — early attunement produces a visible spark but no effect.
    if (!this.deps.rollSkillSuccess('pulse')) {
      this._fizzles++;
      return false;
    }

    // Break Hide on activation.
    this.deps.breakHide?.();

    // Resolve upgrade-modified stats (scope: 'pulse').
    this._resolvedRangeTiles =
      this.config.baseRangeTiles *
      this.deps.resolver.getMultiplicativeEffect('pulse', 'rangeMultiplier');

    this._resolvedDisruptDurationMs =
      this.config.baseDisruptDurationMs +
      this.deps.resolver.getAdditiveEffect('pulse', 'disruptDurationMs');

    // Fire the EM burst.
    const hitResult = this.deps.aoe.hit({
      rangeTiles: this._resolvedRangeTiles,
      radiusTiles: this.config.baseRadiusTiles,
      damageMultiplier: 1.0,
      sourceSkillId: 'pulse',
    });

    this._activations++;

    if (hitResult.enemiesHit > 0) {
      this._hitsLanded++;

      // Consume Static charge — bonus damage applied to the burst.
      const consumed = this.deps.charge.consume('static_charge');
      this._totalChargeConsumed += consumed;

      // Interrupt any in-progress attack or ability on the hit enemy.
      this.deps.interruptEnemy?.();
      this._interrupts++;

      // Brief stun — check immunity first (stun has a 300ms immunity window).
      if (!this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'pulse',
          durationMs: this.config.baseStunDurationMs,
        });
        this._stunApplications++;
      }

      // Disruption — no immunity window; apply unconditionally.
      // STUB: deferred — no enemy ability/cast system reads disruption
      this.deps.statusEffects.apply('disruption', {
        sourceSkillId: 'pulse',
        durationMs: this._resolvedDisruptDurationMs,
      });
      this._disruptApplications++;

      // Resonant Pulse (pulse_field): spawn em_residue zone at impact point.
      // Enemies entering the zone receive periodic disruption (no immunity window).
      // Integration layer routes dot_tick events for 'disruption' to StatusEffectSystem.
      const residueRadiusTiles = this.deps.resolver.getAdditiveEffect('pulse', 'residueRadiusTiles');
      if (residueRadiusTiles > 0 && this.deps.zones) {
        const residueDurationMs = this.deps.resolver.getAdditiveEffect('pulse', 'residueDurationMs');
        const residueDisruptMs = this.deps.resolver.getAdditiveEffect('pulse', 'residueDisruptMs');
        this.deps.zones.create({
          type: 'em_residue',
          x: hitResult.hitX,
          y: hitResult.hitY,
          radiusTiles: residueRadiusTiles,
          durationMs: residueDurationMs > 0 ? residueDurationMs : 1500,
          tickIntervalMs: 200,
          dotDefId: 'disruption',
          dotDurationMs: residueDisruptMs > 0 ? residueDisruptMs : 300,
          sourceSkillId: 'pulse',
        });
        this._zoneSpawns++;
      }
    }

    // Start cooldown.
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
